import * as THREE from "three";
import { isString } from "mathjs";
import Action from '../../classes/Action';
import AssetUtility from '../../classes/AssetUtility';
import Engine from '../../classes/Engine';
import { SaveModificationTypes, TrackerTypes, CustomEventTypes, RenderModes } from '../../globals';
import ARTracker from "../../classes/ARTracker";
import SceneObjectUtils from '../../classes/utilities/SceneObjectUtils';
import ProjectSaver from '../../classes/ProjectSaver';
import {assert} from '../../classes/utilities/ErrorHandling';
import DefaultSceneManager from '../../classes/DefaultSceneManager';
import TransformUtils from '../../classes/utilities/TransformUtils';
import VisibilityManager from '../../classes/VisibilityManager';
import { IObjectSelection } from '../../interfaces/Misc';
import MaterialAsset from "../../classes/MaterialAsset";
import { GetTimelines } from '../../classes/timeline';
import { Timeline } from '../../classes/timeline/Timeline';
import TimelineObject from "../../classes/timeline/TimelineObject";
import Scenario from '../../classes/scenarioElements/Scenario';
import Callout from '../../classes/Callout';
import { ITrackerSaveData, ITrackerSceneSelectionNodeData } from "../../interfaces/ProjectSave";
import StandbyOverlay from "../../components/layout/StandbyOverlay";

export interface IPrimitiveIndexMap {
	nodeIndex: number;
	primitiveIndex: number;
}

export type IOldNodesToNewLinkInformation = Record<number, Record<number, IPrimitiveIndexMap>>;

interface ITrackerSceneSelectionNode {
	nodeIndex: number;
	primitiveIndex: number;
	assetId: string;
	position: number[];
	quaternion: THREE.QuaternionTuple;
	scale: number[];
	children: ITrackerSceneSelectionNode[];
}

interface ITrackerSceneSelectionSwapData {
	originalTracker: ARTracker
	newTracker?: ARTracker;
}

interface INodeSwapData {
	originalNode: THREE.Object3D;
	newNode?: THREE.Object3D;
	originalNodeParent? : THREE.Object3D;
	sceneGuid: string;
	requiresSwap: boolean;
}

interface INodeReparentData {
	node: THREE.Object3D;
	originalParent: THREE.Object3D;
	newParent: THREE.Object3D;
	atIndex : number;
}

interface ITimelineSwapData{
	timelineObject: TimelineObject;
	originalSceneObject: THREE.Object3D;
	newSceneObject?: THREE.Object3D;
}

interface ICalloutSwapData{
	callout: Callout;
	originalSceneObject: THREE.Object3D;
	newSceneObject: THREE.Object3D;
}

interface IMaterialSwapData{
	originalMaterialAsset?: MaterialAsset;
	newMaterialAsset?: MaterialAsset;
	objectGuid: string,
}

interface IMaterialInstanceSwapData{
	originalSceneObject: THREE.Object3D;
	newSceneObject: THREE.Object3D;
}

interface IDefaultObjectsSwapData{
	originalDefaultObject: THREE.Object3D;
	newDefaultObject: THREE.Object3D | null;
	defaultSceneGuid: string;
	originalLinkedObjects: THREE.Object3D[];
	newLinkedObjects: (THREE.Object3D | null)[];
	linkedScenes: THREE.Scene[];
}

interface IRemovedLinkedObjectData{
	object: THREE.Object3D;
	sceneGuid: string;
}


export default class ReplaceModelAsset extends Action
{
	private initialized = false;
	private oldNodesToNewLinkInformation : IOldNodesToNewLinkInformation = {};
	private sceneGuidToAssetSaveRoot = new Map<string, THREE.Object3D[]>();
	private sceneGuidToDefaultObjectGuids: Map<string, string[]> = new Map();
	private modelTrackersWithAssetReference: ARTracker[] = [];
	private modelTrackersWithSceneSelection: ITrackerSceneSelectionSwapData[] = [];
	private nodeSwapDatabase: INodeSwapData[] = [];
	private nodeReparentDatabase: INodeReparentData[] = [];
	private timelineSwapDatabase: ITimelineSwapData[] = [];
	private calloutSwapDatabase: ICalloutSwapData[] = [];
	private materialSwapDatabase: IMaterialSwapData[] = [];
	private materialInstanceSwapDatabase: IMaterialInstanceSwapData[] = [];
	private defaultObjectsSwapData: IDefaultObjectsSwapData[] = [];
	private removedLinkedObjectsData: IRemovedLinkedObjectData[] = [];
	
	constructor(oldAssetId: string, newAssetId: string, oldNodesToNewLinkInformation: IOldNodesToNewLinkInformation, keepOriginalMaterials : boolean)
	{
		const oldAssetName = AssetUtility.getAssetName(oldAssetId);
		const newAssetName = AssetUtility.getAssetName(newAssetId);
		const actionName = "Replaced " + oldAssetName + " References With " + newAssetName;

		const previousSelection = Engine.getInstance().getPickingManager().getCurrentSelection();
	
		super([newAssetId, oldAssetId, oldNodesToNewLinkInformation, true, keepOriginalMaterials],
			  [newAssetId, oldAssetId, oldNodesToNewLinkInformation, false, keepOriginalMaterials, previousSelection], 
			  actionName,
			  [oldAssetId, newAssetId]);
	}

	do(newAssetId: string, oldAssetId: string, oldNodesToNewLinkInformation: IOldNodesToNewLinkInformation, oldToNew: boolean, keepOriginalMaterials: boolean, previousSelection: IObjectSelection | undefined = undefined) : void
	{
		StandbyOverlay.startStandbyMode();
		setTimeout(() =>
		{
			const timeline = Scenario.currentStep()?.timeline;
			if (timeline) timeline.resetAnimations();

			if(!this.initialized)
			{
				this.initialize(oldAssetId, newAssetId, oldNodesToNewLinkInformation, keepOriginalMaterials);
			}

			for(const reparentData of this.nodeReparentDatabase)
			{
				const newParent = oldToNew ? reparentData.newParent : reparentData.originalParent;
				newParent.add(reparentData.node);
				const atIndex = reparentData.atIndex;
				newParent.children.splice(atIndex, 0, newParent.children.splice(newParent.children.length-1, 1)[0]);
			}
			
			for(const swapData of this.defaultObjectsSwapData)
			{
				const addedDefaultObject = oldToNew ? swapData.newDefaultObject : swapData.originalDefaultObject;
				const addedLinkedObjects = oldToNew ? swapData.newLinkedObjects : swapData.originalLinkedObjects;
				const removedDefaultObject = oldToNew ? swapData.originalDefaultObject : swapData.newDefaultObject;
				const removedLinkedObjects = oldToNew ? swapData.originalLinkedObjects : swapData.newLinkedObjects;

				DefaultSceneManager.getInstance().onObjectAssetSwap(removedDefaultObject, removedLinkedObjects, addedDefaultObject, addedLinkedObjects, 
					swapData.linkedScenes, oldToNew ? oldAssetId : newAssetId, oldToNew ? newAssetId : oldAssetId);

				for(let i = 0; i < removedLinkedObjects.length; i++)
				{
					const removedLinkedObject = removedLinkedObjects[i];
					if(removedLinkedObject && !removedLinkedObject.userData.removedFromScene)
					{
						const linkedSceneGuid = swapData.linkedScenes[i].userData.guid;
						Engine.getInstance().removeObject(removedLinkedObject, false, linkedSceneGuid);
					}
				}
				for(let i = 0; i < addedLinkedObjects.length; i++)
				{
					const addedLinkedObject = addedLinkedObjects[i];
					if(addedLinkedObject && !addedLinkedObject.userData.removedFromScene)
					{
						addedLinkedObject.userData.removedFromScene = true;
					}
				}
			}

			for(const swapData of this.nodeSwapDatabase)
			{
				const nodeToAdd = oldToNew ? swapData.newNode : swapData.originalNode;
				const nodeToRemove = oldToNew ? swapData.originalNode : swapData.newNode;

				if (nodeToRemove)
				{
					if (nodeToAdd)
					{
						const parent = nodeToRemove.parent!
						nodeToAdd.userData.removedFromScene = true;
						Engine.getInstance().addObject(nodeToAdd, parent, swapData.sceneGuid, true);
						const atIndex = parent.children.indexOf(nodeToRemove!);
						parent.children.splice(atIndex, 0, parent.children.splice(parent.children.length-1, 1)[0]);
					}
					Engine.getInstance().removeObject(nodeToRemove, false, swapData.sceneGuid);
				}
				else
				{	
					assert(nodeToAdd && swapData.originalNodeParent);
					Engine.getInstance().addObject(nodeToAdd!, swapData.originalNodeParent!, swapData.sceneGuid, true);
					swapData.originalNodeParent!.add(nodeToAdd!);
				}
			}	

			if(!oldToNew)
			{
				//when this action switches back to the old asset, adding any default object will re-add all its linked counterparts back into the scene.
				//we therefore stored whichever linked counteparts which were removed from the scene prior to this action, and will now make sure they are removed.
				for(const linkedObjectData of this.removedLinkedObjectsData)
				{
					Engine.getInstance().removeObject(linkedObjectData.object, false, linkedObjectData.sceneGuid);

				}
			}
			
			this.swapModelTrackerReferences(oldToNew, newAssetId, oldAssetId);
			if (timeline) timeline.resetAnimations();
			this.swapTimelineReferences(oldToNew);
			this.swapCalloutReferences(oldToNew);
			this.swapMaterialReferences(oldToNew);

			if(previousSelection)
			{
				Engine.getInstance().getPickingManager().restoreSelection(previousSelection);
			}
			else
			{
				Engine.getInstance().getPickingManager().clearSelection();
			}
			Engine.getInstance().getPickingManager().storeSelection();

			if (timeline) timeline.update();

			VisibilityManager.setObjectsVisibilityMode([Engine.getInstance().getSelectionRoot()], [Engine.getInstance().getSelectionRoot().userData.visibility[VisibilityManager.getRenderMode()]]);
			document.dispatchEvent(new CustomEvent(CustomEventTypes.objectSelectionChanged));
			StandbyOverlay.stopStandbyMode();
		}, 0);
	}

	initialize(oldAssetId: string, newAssetId: string, oldNodesToNewLinkInformation: IOldNodesToNewLinkInformation, keepOriginalMaterials: boolean) : void
	{
		this.oldNodesToNewLinkInformation = oldNodesToNewLinkInformation;
		this.gatherAssetReferences(oldAssetId);
		this.generateNewSaveRoots(newAssetId, oldAssetId, keepOriginalMaterials);
		this.generateSceneSelectionTrackers(newAssetId, oldAssetId);
		this.nodeSwapDatabase = this.nodeSwapDatabase.filter((swapData) => { return swapData.requiresSwap; }); //nodes that didn't require swapping were only part of the database to help with reparenting
		this.nodeSwapDatabase.reverse(); //database is built from top to bottom. we want to process it from bottom to top in order to first handle any children and only later their parents
		this.removedLinkedObjectsData.reverse();
		this.initialized = true;
	}

	gatherAssetReferences(assetId : string)
	{
		const traverseGatherAssetReferences = (scene : THREE.Scene, object: THREE.Object3D)  =>
		{
			if(object instanceof ARTracker && (object as ARTracker).trackerType === TrackerTypes.MODEL)
			{
				if((object as ARTracker).modelAssetId === assetId)
				{
					this.modelTrackersWithAssetReference.push(object);
				} 
				else if((object as ARTracker).sceneSelectionModels.length)
				{
					for(const sceneSelectionModel of (object as ARTracker).sceneSelectionModels)
					{
						if(sceneSelectionModel.userData.assetId === assetId)
						{
							this.modelTrackersWithSceneSelection.push({originalTracker: object as ARTracker});
							break;
						}
					}
				}
				return;
			}

			if(object.userData.markedSaveRoot && object.userData.assetId === assetId && !DefaultSceneManager.getInstance().getDefaultObject(object.userData.guid))
			{
				this.sceneGuidToAssetSaveRoot.get(scene.userData.guid)!.push(object);
				if (scene.userData.isDefaultScene)
				{
					this.sceneGuidToDefaultObjectGuids.get(scene.userData.guid)!.push(object.userData.guid);
				} 
			}

			for (const child of object.children)
			{
				traverseGatherAssetReferences(scene, child);
			}
		};
			
		const sceneList = Engine.getInstance().getSceneList() as THREE.Scene[];
		sceneList.sort((a, b) => {	return a.userData.isDefaultScene ? -1 : 1;	});
		for (const scene of sceneList)
		{
			const sceneGuid = scene.userData.guid;
			const sceneRoot = Engine.getInstance().getSelectionRoot(sceneGuid);

			this.sceneGuidToAssetSaveRoot.set(sceneGuid, []);
			if (scene.userData.isDefaultScene)
			{
				this.sceneGuidToDefaultObjectGuids.set(sceneGuid, []);
			} 

			traverseGatherAssetReferences(scene, sceneRoot);
		}
	}

	generateNewSaveRoots(newAssetId: string, oldAssetId: string, keepOriginalMaterials: boolean)
	{
		//for each saveRoot of the old asset, create a parallel saveRoot of the new asset and store it in a map so that they can be replaced with one another
		const newAssetRoot = Engine.getInstance().getModelAsset(newAssetId) as THREE.Object3D;
		const oldAssetRoot = Engine.getInstance().getModelAsset(oldAssetId) as THREE.Object3D;
		this.sceneGuidToAssetSaveRoot.forEach((assetSaveRoots, sceneGuid) =>
		{
			for(const assetSaveRoot of assetSaveRoots)
			{	
				const nodeIndex = assetSaveRoot.userData.modelData.nodeIndex;
				const primitiveIndex = assetSaveRoot.userData.modelData.primitiveIndex;
				const newNodeData = this.oldNodesToNewLinkInformation[nodeIndex] ? this.oldNodesToNewLinkInformation[nodeIndex][primitiveIndex] : undefined;
				const isDefaultObject = DefaultSceneManager.getInstance().isDefaultObject(assetSaveRoot.userData.guid);

				let newAssetSaveRoot: THREE.Object3D | undefined = undefined;
				const nodeSwapData: INodeSwapData = {
					originalNode: assetSaveRoot, 
					originalNodeParent: assetSaveRoot.parent!, 
					sceneGuid: sceneGuid,
					requiresSwap: true,
				};
				const defaultObjectSwapData: IDefaultObjectsSwapData | undefined = isDefaultObject ? 
					{
						originalDefaultObject: assetSaveRoot, 
						newDefaultObject: null, 
						defaultSceneGuid: sceneGuid, 
						originalLinkedObjects: [], 
						newLinkedObjects: [], 
						linkedScenes: [] 
					} : undefined;


				if(newNodeData)
				{
					const newAssetSourceNode = SceneObjectUtils.getSubNode(newAssetRoot, newNodeData.nodeIndex, newNodeData.primitiveIndex, newAssetId)!;
					const oldAssetSourceNode = SceneObjectUtils.getSubNode(oldAssetRoot, nodeIndex, primitiveIndex, oldAssetId)!;
					newAssetSaveRoot = this.initializeSaveRootNode(newAssetSourceNode, oldAssetSourceNode, assetSaveRoot, keepOriginalMaterials, sceneGuid);
					nodeSwapData.newNode = newAssetSaveRoot;
					if(isDefaultObject)
					{
						defaultObjectSwapData!.newDefaultObject = newAssetSaveRoot;
					}
					
					if(assetSaveRoot.parent?.userData.assetId === assetSaveRoot.userData.assetId)
					{
						//if the parent of the node we are attempting to swap will itself get replaced, it means
						//we can find that parent's saveRoot within this.nodeSwapDatabase, find the subnode the parent will be replaced with
						//and immidiately add our new saveRoot as its child, without needing to perform any swaps.
						const parentSaveRoot = ProjectSaver.getInstance().getParentSaveRoot(assetSaveRoot);
						const parentNodeIndex = assetSaveRoot.parent?.userData.modelData.nodeIndex;
						const parentPrimitiveIndex = assetSaveRoot.parent?.userData.modelData.primitiveIndex;
						const parentSubnodeData = this.oldNodesToNewLinkInformation[parentNodeIndex!] ? this.oldNodesToNewLinkInformation[parentNodeIndex!][parentPrimitiveIndex!] : undefined;

						if(parentSubnodeData)
						{
							for(let i = this.nodeSwapDatabase.length - 1; i >= 0; i--) //iterating in reverse because what we're looking for is more likely to be found near the end of the array
							{
								const swapData = this.nodeSwapDatabase[i];
								if(swapData.originalNode === parentSaveRoot)
								{
									const parent = SceneObjectUtils.getSubNode(swapData.newNode!, parentSubnodeData.nodeIndex, parentSubnodeData.primitiveIndex, newAssetId);
									parent!.add(newAssetSaveRoot);
									nodeSwapData.requiresSwap = false;
									break;
								}
								else if(swapData.originalNode === DefaultSceneManager.getInstance().getDefaultObject(parentSaveRoot.userData.guid))
								{
									for(const defaultSwapData of this.defaultObjectsSwapData)
									{
										if(defaultSwapData.originalDefaultObject === swapData.originalNode)
										{
											for(let j = defaultSwapData.linkedScenes.length - 1; j >= 0; j--) //iterating in reverse because what we're looking for is more likely to be found near the end of the array
											{
												if(defaultSwapData.linkedScenes[j].userData.guid === sceneGuid)
												{
													const linkedSaveRoot = defaultSwapData.newLinkedObjects[j];
													const linkedParent = SceneObjectUtils.getSubNode(linkedSaveRoot!, parentSubnodeData.nodeIndex, parentSubnodeData.primitiveIndex, newAssetId)!;
													linkedParent.add(newAssetSaveRoot);
													nodeSwapData.requiresSwap = false;
													break;
												}
											}
											if(!nodeSwapData.requiresSwap) break;
										}
									}
									if(!nodeSwapData.requiresSwap) break;
								}
							}
						}
					}
				}
				else if(assetSaveRoot.parent?.userData.assetId === assetSaveRoot.userData.assetId)
				{
					nodeSwapData.requiresSwap = false;
				}
				
				this.nodeSwapDatabase.push(nodeSwapData);


				if(isDefaultObject)
				{
					const linkedScenes = DefaultSceneManager.getInstance().getLinkedScenes(sceneGuid) as THREE.Scene[];
					for(const linkedScene of linkedScenes)
					{
						const linkedAssetRoot = DefaultSceneManager.getInstance().getLinkedObject(assetSaveRoot, linkedScene.userData.guid);
						defaultObjectSwapData!.originalLinkedObjects.push(linkedAssetRoot!);
						defaultObjectSwapData!.linkedScenes.push(linkedScene);

						if(!linkedAssetRoot || linkedAssetRoot.userData.removedFromScene)
						{
							defaultObjectSwapData!.newLinkedObjects.push(null);
							if(linkedAssetRoot && linkedAssetRoot.userData.removedFromScene)
							{
								this.removedLinkedObjectsData.push({object: linkedAssetRoot, sceneGuid: linkedScene.userData.guid});
							}
						} 
						else if(newNodeData)
						{
							const newNode = this.initializeSaveRootNode(newAssetSaveRoot!, assetSaveRoot, linkedAssetRoot, keepOriginalMaterials, linkedScene.userData.guid); 
							defaultObjectSwapData!.newLinkedObjects.push(newNode);
							
							if(!nodeSwapData.requiresSwap)
							{
								assert(newAssetSaveRoot!.parent);
								const parentSaveRoot = ProjectSaver.getInstance().getParentSaveRoot(assetSaveRoot);
								for(const swapData of this.defaultObjectsSwapData)
								{
									if(swapData.originalDefaultObject === parentSaveRoot)
									{
										for(let i = 0; i < swapData.linkedScenes.length; i++)
										{
											if(swapData.linkedScenes[i].userData.guid === linkedScene.userData.guid)
											{
												const linkedSaveRoot = swapData.newLinkedObjects[i];
												const linkedParent = SceneObjectUtils.getSubNode(linkedSaveRoot!, newAssetSaveRoot!.parent!.userData.modelData.nodeIndex, newAssetSaveRoot!.parent!.userData.modelData.primitiveIndex, newAssetId)!;
												linkedParent.add(newNode);
												break;
											}
										}
										break;
									}
								}
							}
						}

					}
				}
				if(defaultObjectSwapData) this.defaultObjectsSwapData.push(defaultObjectSwapData);
			}
		});
	}

	generateSceneSelectionTrackers(newAssetId: string, oldAssetId: string)
	{
		const newAssetRoot = Engine.getInstance().getModelAsset(newAssetId) as THREE.Object3D;
		const oldAssetRoot = Engine.getInstance().getModelAsset(oldAssetId) as THREE.Object3D;

		//we can't assume that the original scene objects used to construct the model tracker are still in the scene, so scene selection trackers  must have their own isolated swap logic;
		//we first gather the selection nodes save data and then flatten it such that each nodes will hold a world transform rather than local.
		//once our data is flat, we perform a one-to-one node swap, while applying the transform deltas between the old and new asset sources.
		//finally, we call loadSceneSelection to load the flattened data into the new tracker.
			
		const flattenNode = (node: ITrackerSceneSelectionNodeData, flatNodes: ITrackerSceneSelectionNodeData[], parentWorldTransform: any) =>
		{
			const localTransform = {
				position: new THREE.Vector3().fromArray(node.position),
				quaternion: new THREE.Quaternion().fromArray(node.quaternion),
				scale: new THREE.Vector3().fromArray(node.scale)
			};
			
			// Combine local transform with parent world transform to compute the current world transform.
			let worldTransform;
			if (parentWorldTransform) 
			{
				worldTransform = {
					position: parentWorldTransform.position.clone().add(localTransform.position),
					quaternion: parentWorldTransform.quaternion.clone().multiply(localTransform.quaternion),
					scale: parentWorldTransform.scale.clone().multiply(localTransform.scale)
				};
			}
			else 
			{
				worldTransform = localTransform;
			}
	
			const flatNodeData : ITrackerSceneSelectionNodeData = JSON.parse(JSON.stringify(node));
			flatNodeData.position = worldTransform.position.toArray();
			flatNodeData.quaternion = worldTransform.quaternion.toArray();
			flatNodeData.scale = worldTransform.scale.toArray();
			flatNodeData.children = [];
	
			flatNodes.push(flatNodeData);
	
			for (const child of node.children)
			{
				flattenNode(child, flatNodes, worldTransform);
			}
		}

		const modifySelectionNode = (node: ITrackerSceneSelectionNode, isRootNode: boolean) =>
		{
			let shouldKeepNode = true;
			if(node.assetId === oldAssetId)
			{
				const parallelNodeData = this.oldNodesToNewLinkInformation[node.nodeIndex] ? this.oldNodesToNewLinkInformation[node.nodeIndex][node.primitiveIndex] : undefined;
				if(parallelNodeData)
				{
					const oldAssetSourceNode = SceneObjectUtils.getSubNode(oldAssetRoot, node.nodeIndex, node.primitiveIndex, oldAssetId)!;
					const newAssetSourceNode = SceneObjectUtils.getSubNode(newAssetRoot, parallelNodeData.nodeIndex, parallelNodeData.primitiveIndex, newAssetId)!;

					const oldWorldPosition = new THREE.Vector3();
					const oldWorldQuaternion = new THREE.Quaternion();
					const oldWorldScale = new THREE.Vector3();
					const newWorldPosition = new THREE.Vector3();
					const newWorldQuaternion = new THREE.Quaternion();
					const newWorldScale = new THREE.Vector3();

					oldAssetSourceNode.getWorldPosition(oldWorldPosition);
					oldAssetSourceNode.getWorldQuaternion(oldWorldQuaternion);
					oldAssetSourceNode.getWorldScale(oldWorldScale);
					newAssetSourceNode.getWorldPosition(newWorldPosition);
					newAssetSourceNode.getWorldQuaternion(newWorldQuaternion);
					newAssetSourceNode.getWorldScale(newWorldScale);

					const positionDelta = newWorldPosition.sub(oldWorldPosition) as THREE.Vector3;
					const rotationDelta = newWorldQuaternion.multiply(oldWorldQuaternion.invert()) as THREE.Quaternion;
					const scaleDelta = newWorldScale.divide(oldWorldScale) as THREE.Vector3;
					
					node.assetId = newAssetId;
					node.nodeIndex = parallelNodeData.nodeIndex;
					node.primitiveIndex = parallelNodeData.primitiveIndex;
					node.position = new THREE.Vector3().fromArray(node.position).add(positionDelta).toArray();
					node.quaternion = new THREE.Quaternion().fromArray(node.quaternion).multiply(rotationDelta).toArray();			
					node.scale = new THREE.Vector3().fromArray(node.scale).multiply(scaleDelta).toArray();
				}
				else
				{
					shouldKeepNode = false;
					node.children.length = 0;
				}
			}

			for(const child of node.children)
			{
				modifySelectionNode(child, false);
			}

			if(isRootNode)
			{
				return shouldKeepNode;
			}
		}
		
		for(const trackerSwapData of this.modelTrackersWithSceneSelection)
		{
			const selectionSaveData: ITrackerSaveData = {
				name: trackerSwapData.originalTracker.name,
				id: trackerSwapData.originalTracker.userData.guid,
				type: TrackerTypes.MODEL,
				displayMode: RenderModes.Device,
				translation: {x: 0, y: 0, z: 0},
				rotation: {x: 0, y: 0, z: 0, w: 1},
				guideImageAssetId: trackerSwapData.originalTracker.guideImageAssetId,
				guideTitle: trackerSwapData.originalTracker.guideTitle,
				guideTitleVisible: trackerSwapData.originalTracker.guideTitleVisible,
				guideCaption: trackerSwapData.originalTracker.guideCaption,
				guideCaptionVisible: trackerSwapData.originalTracker.guideCaptionVisible,
				guideSize: trackerSwapData.originalTracker.guideSize,
			};
			ARTracker.saveSceneSelection(selectionSaveData, trackerSwapData.originalTracker, new Set<string>());

			const flattenedSelectionSaveData: ITrackerSaveData = {
				name: trackerSwapData.originalTracker.name,
				id: trackerSwapData.originalTracker.userData.guid,
				type: TrackerTypes.MODEL,
				displayMode: RenderModes.Device,
				translation: {x: 0, y: 0, z: 0},
				rotation: {x: 0, y: 0, z: 0, w: 1},
				sceneSelectionModels: [],
				guideImageAssetId: trackerSwapData.originalTracker.guideImageAssetId,
				guideTitle: trackerSwapData.originalTracker.guideTitle,
				guideTitleVisible: trackerSwapData.originalTracker.guideTitleVisible,
				guideCaption: trackerSwapData.originalTracker.guideCaption,
				guideCaptionVisible: trackerSwapData.originalTracker.guideCaptionVisible,
				guideSize: trackerSwapData.originalTracker.guideSize,
			};

			for(const selectectionModel of selectionSaveData.sceneSelectionModels!)
			{
				flattenNode(selectectionModel, flattenedSelectionSaveData.sceneSelectionModels!, null);
			}

			if(flattenedSelectionSaveData.sceneSelectionModels?.length)
			{
				for(let i = flattenedSelectionSaveData.sceneSelectionModels.length-1; i >= 0; i--)
				{
					const node = flattenedSelectionSaveData.sceneSelectionModels[i] as ITrackerSceneSelectionNode;
					const keepNode = modifySelectionNode(node, true);
					if(!keepNode)
					{
						flattenedSelectionSaveData.sceneSelectionModels.splice(i, 1);
					}
				}
			
				if(flattenedSelectionSaveData.sceneSelectionModels.length)
				{
					const newTracker = SceneObjectUtils.cloneObject(trackerSwapData.originalTracker) as ARTracker;
					ARTracker.loadSceneSelection(flattenedSelectionSaveData, newTracker);
					newTracker.position.copy(trackerSwapData.originalTracker.position);
					newTracker.quaternion.copy(trackerSwapData.originalTracker.quaternion);
					trackerSwapData.newTracker = newTracker;
				}
			}
			
		}
	}

	initializeSaveRootNode(newAssetSource: THREE.Object3D, oldAssetSource: THREE.Object3D, replacedNode: THREE.Object3D, keepOriginalMaterials: boolean, sceneGuid: string) : THREE.Object3D
	{
		const newNode = SceneObjectUtils.cloneObject(newAssetSource) as THREE.Object3D;
		ProjectSaver.getInstance().markAsSaveRoot(newNode);

		const traverseGatherNodesToReparent = (object: THREE.Object3D)  =>
		{
			if(object.userData.hideInHierarchy) return;
			//the hierarchy of the saveRoot we're replacing may contain:
			//a. objects that aren't part of the asset (primtiives, other assets, etc)
			//b. saveRoots of the same asset
			
			//as we swap our saveRoots, these nodes will need to know which post-swap parent to belong to.
			//we can't just use the parent of the saveRoot we're replacing, because that parent will be removed.
			//instead, we find the parallel node in the new asset and use that as the parent. if no such node exists, use the new saveRoot as the parent.
			if(object.userData.assetId !== oldAssetSource.userData.assetId && object.userData.markedSaveRoot)
			{
				const reparentData : INodeReparentData = {node: object, originalParent: object.parent!, newParent: newNode, atIndex: object.parent!.children.indexOf(object)};

				const parentNodeIndex = object.parent!.userData.modelData.nodeIndex;
				const parentPrimitiveIndex = object.parent!.userData.modelData.primitiveIndex;
				const parentSubnodeData = this.oldNodesToNewLinkInformation[parentNodeIndex] ? this.oldNodesToNewLinkInformation[parentNodeIndex][parentPrimitiveIndex] : undefined;
				if(parentSubnodeData)
				{
					const newParent = SceneObjectUtils.getSubNode(newNode, parentSubnodeData.nodeIndex, parentSubnodeData.primitiveIndex, newAssetSource.userData.assetId);
					if(newParent)
					{
						reparentData.newParent = newParent;
					}
				}

				this.nodeReparentDatabase.push(reparentData);
			}
			else
			{
				for (const child of object.children)
				{
					traverseGatherNodesToReparent(child);
				}
			}
		};

		for(const child of replacedNode.children)
		{
			traverseGatherNodesToReparent(child);
		}
	
		if(replacedNode.userData.removedSubNodes)
		{
			newNode.userData.removedSubNodes = [];
			for(const removedSubNodeData of replacedNode.userData.removedSubNodes)
			{
				const subnodeToRemoveData = this.oldNodesToNewLinkInformation[removedSubNodeData.nodeIndex] ? this.oldNodesToNewLinkInformation[removedSubNodeData.nodeIndex][removedSubNodeData.primitiveIndex] : undefined;
				if(subnodeToRemoveData)
				{
					const subnodeToRemove = SceneObjectUtils.getSubNode(newNode, subnodeToRemoveData.nodeIndex, subnodeToRemoveData.primitiveIndex, newNode.userData.assetId);
					if(subnodeToRemove)
					{
						subnodeToRemove.removeFromParent();
						SceneObjectUtils.disposeObject(subnodeToRemove, false);
						newNode.userData.removedSubNodes.push({nodeIndex: subnodeToRemoveData.nodeIndex, primitiveIndex: subnodeToRemoveData.primitiveIndex});
					}
				}
			}
		}

		const timelines = GetTimelines() as Timeline[];
		const timeline = timelines.find((timeline) => { return timeline.associatedSceneId === sceneGuid; });
		
		const traverseApplySaveModifications = (sourceObject: THREE.Object3D)  =>
		{
			//for each child of the replacedNode, check to see if there is a parallel node in the new asset.
			//if such exists - apply any saveMoficiations to it.
			if(sourceObject.userData.hideInHierarchy || sourceObject.userData.assetId !== oldAssetSource.userData.assetId || (sourceObject.userData.markedSaveRoot && sourceObject !== replacedNode)) return;

			const sourceNodeIndex = sourceObject.userData.modelData.nodeIndex;
			const sourcePrimitiveIndex = sourceObject.userData.modelData.primitiveIndex;
			const targetNodeData = this.oldNodesToNewLinkInformation[sourceNodeIndex] ? this.oldNodesToNewLinkInformation[sourceNodeIndex][sourcePrimitiveIndex] : undefined;
			let targetObject : THREE.Object3D | undefined = undefined;
			if(targetNodeData)
			{
				targetObject = SceneObjectUtils.getSubNode(newNode, targetNodeData.nodeIndex, targetNodeData.primitiveIndex, newNode.userData.assetId)!;
				if(targetObject)
				{
					const sourceReferenceNode = SceneObjectUtils.getSubNode(oldAssetSource, sourceNodeIndex, sourcePrimitiveIndex, oldAssetSource.userData.assetId)!;
					this.applySaveModifications(sourceObject, targetObject, sourceReferenceNode, keepOriginalMaterials, timeline);
				}
			}

			for (const child of sourceObject.children)
			{
				traverseApplySaveModifications(child);
			}
		};

		traverseApplySaveModifications(replacedNode);

		return newNode;
	}

	applySaveModifications(sourceNode: THREE.Object3D, targetNode: THREE.Object3D, sourceReferenceNode: THREE.Object3D, keepOriginalMaterials: boolean, timeline: Timeline | undefined = undefined) : void
	{
		if(keepOriginalMaterials)
		{
			const sourceNodeMaterialAsset = MaterialAsset.getMaterialAssetByObjectGuid(sourceNode.userData.guid);
			const targetNodeMaterialAsset = MaterialAsset.getMaterialAssetByObjectGuid(targetNode.userData.guid);

			if(sourceNodeMaterialAsset && targetNodeMaterialAsset)
			{
				MaterialAsset.assignMaterialAssetToObject(sourceNodeMaterialAsset, targetNode as THREE.Mesh);
				ProjectSaver.getInstance().markModifiedObject(targetNode, SaveModificationTypes.Material);
			}
		}

		const swapNodeReferences = () =>
		{
			const guidToDiscard = targetNode.userData.guid as string;
			const guidToAssign = sourceNode.userData.guid as string;
			if (targetNode.userData.animations)
			{
				Engine.getInstance().deleteAnimationMixer(targetNode);
			}
			
			const targetMaterialAsset = MaterialAsset.getMaterialAssetByObjectGuid(targetNode.userData.guid);
			if(targetMaterialAsset)
			{
				MaterialAsset.overrideObjectGuidMaterialAssetReference(guidToDiscard);
				MaterialAsset.overrideObjectGuidMaterialAssetReference(guidToAssign, targetMaterialAsset);

				//if this object had material instances, must make the instances point at the new object
				const materialInstance = MaterialAsset.getTimelineMaterialInstance(sourceNode);
				if(materialInstance)
				{
					const materialInstanceSwapData : IMaterialInstanceSwapData = {
						originalSceneObject: sourceNode,
						newSceneObject: targetNode
					}

					this.materialInstanceSwapDatabase.push(materialInstanceSwapData);
				}

				const sourceMaterialAsset = MaterialAsset.getMaterialAssetByObjectGuid(sourceNode.userData.guid);
				if(sourceMaterialAsset !== targetMaterialAsset)
				{
					const materialSwapData : IMaterialSwapData = { 
						originalMaterialAsset: sourceMaterialAsset, 
						newMaterialAsset: MaterialAsset.getMaterialAssetByObjectGuid(targetNode.userData.guid), 
						objectGuid: guidToAssign
					}
	
					this.materialSwapDatabase.push(materialSwapData);
				}
			}
								
			targetNode.userData.guid = guidToAssign;
		
			if(timeline)
			{
				const objectsTrack = timeline.objectsTrack!;
				for (const timelineObject of objectsTrack.objects)
				{
					if (timelineObject.sceneObjectGuid === guidToAssign)
					{
						this.timelineSwapDatabase.push({timelineObject: timelineObject, originalSceneObject: sourceNode, newSceneObject: targetNode});
					}
				}
			}
			
			if (targetNode.userData.animations)
			{
				Engine.getInstance().createAnimationMixer(targetNode);
			}
		}

		const applyModification = (modificationType: SaveModificationTypes)  =>
		{
			switch(modificationType)
			{
				case SaveModificationTypes.Position:
					const deltaPosition = new THREE.Vector3().subVectors(sourceNode.position, sourceReferenceNode.position);
					targetNode.position.add(deltaPosition);
					break;
				case SaveModificationTypes.Rotation:
					// const deltaRotation = new THREE.Quaternion().multiplyQuaternions(sourceNode.quaternion, sourceReferenceNode.quaternion.clone().invert());
					// targetNode.quaternion.multiply(deltaRotation);
					// WC20-5227: an object should appear to be the same as the swapped version so we copy the rotation value (this seems to be the only issue)
					// no idea why the deltaRotation is not working or why it's even there to begin with
					targetNode.quaternion.copy(sourceNode.quaternion);
					break;
				case SaveModificationTypes.Scale:
					const deltaScale = sourceNode.scale.clone().divide(sourceReferenceNode.scale);
					targetNode.scale.multiply(deltaScale);
					break;
				case SaveModificationTypes.Pivot:
					if (sourceNode.userData.pivot)
					{
						TransformUtils.loadPivot(targetNode, sourceNode.userData.pivot.relativeOffset, null, sourceNode.userData.pivot.space);
					}
					break;
				case SaveModificationTypes.Visibility:
					targetNode.userData.visibility = JSON.parse(JSON.stringify(sourceNode.userData.visibility));
					break;
				case SaveModificationTypes.Name:
					if(sourceNode.name !== sourceReferenceNode.name) targetNode.name = sourceNode.name;
					break;
				case SaveModificationTypes.Trigger:
					if(sourceNode.userData.triggers) targetNode.userData.triggers = JSON.parse(JSON.stringify(sourceNode.userData.triggers));
					break;
				case SaveModificationTypes.Billboard:
					if(sourceNode.userData.billboard) targetNode.userData.billboard = JSON.parse(JSON.stringify(sourceNode.userData.billboard));
					break;
				case SaveModificationTypes.Callout:
					if (sourceNode.userData.annotation)
					{
						targetNode.userData.annotation = JSON.parse(JSON.stringify(sourceNode.userData.annotation));
						if(targetNode.userData.guid === sourceNode.userData.guid)
						{
							this.calloutSwapDatabase.push({callout: Callout.getCallout(sourceNode.userData.guid)!, originalSceneObject: sourceNode, newSceneObject: targetNode});
						}
						else
						{
							Callout.loadCallout(targetNode, targetNode.userData.annotation);
						}
					}
					break;
				case SaveModificationTypes.Groups:
					if(sourceNode.userData.groups) targetNode.userData.groups = JSON.parse(JSON.stringify(sourceNode.userData.groups));
					break;
				case SaveModificationTypes.Metadata:
					if(sourceNode.userData.metaData)
					{
						if(!targetNode.userData.metaData) targetNode.userData.metaData = {};
						for (const key in sourceNode.userData.metaData) 
						{
							if (!sourceNode.userData.metaData[key].readonly) 
							{
								if (!targetNode.userData.metaData[key] || !targetNode.userData.metaData[key].readonly) 
								{
									targetNode.userData.metaData[key] = JSON.parse(JSON.stringify(sourceNode.userData.metaData[key]));
								}
							}
						}
					}							
					break;
				case SaveModificationTypes.ReorderedChildren:
					if(sourceNode.userData.saveModifications && sourceNode.userData.saveModifications.includes(SaveModificationTypes.ReorderedChildren) && sourceNode.children.length)
					{
						for(let i = 0; i < sourceNode.children.length; i++)
						{
							const childNode = sourceNode.children[i];
							if(childNode.userData.hideInHierarchy || childNode.userData.markedSaveRoot) continue;
							const childNodeIndex = childNode.userData.modelData.nodeIndex;
							const childPrimitiveIndex = childNode.userData.modelData.primitiveIndex;
							const targetNodeData = this.oldNodesToNewLinkInformation[childNodeIndex] ? this.oldNodesToNewLinkInformation[childNodeIndex][childPrimitiveIndex] : undefined;
							if(targetNodeData)
							{
								const targetNodeChild = SceneObjectUtils.getSubNode(targetNode, targetNodeData.nodeIndex, targetNodeData.primitiveIndex, targetNode.userData.assetId);
								if(targetNodeChild)
								{
									targetNode.children.splice(i, 0, targetNode.children.splice(targetNode.children.indexOf(targetNodeChild), 1)[0]);
								}
							}
						}
					}
					break;
				case SaveModificationTypes.PartsCatalog:
					if(sourceNode.userData.partsCatalog) targetNode.userData.partsCatalog = JSON.parse(JSON.stringify(sourceNode.userData.partsCatalog));
					break;
				case SaveModificationTypes.Reference:
				case SaveModificationTypes.Material:
					//handled above
					break;
				case SaveModificationTypes.Camera:
				case SaveModificationTypes.Light:
					//technically, we could compare the camera/light data to the reference node's data and apply the difference, but its sounds incredibly unlikely
					//for an author to actually want us to override their model-embedded camera/light values
					break;
				case SaveModificationTypes.Text3D:
				case SaveModificationTypes.Audio:
				case SaveModificationTypes.Removed:
				case SaveModificationTypes.ReAdded:
					//irrelevant to model swapping
					break;
				default:
					console.warn("Unhandled model-swap modification type: " + modificationType);
					break;
			}
		}

		if(sourceNode.userData.markedSaveRoot)
		{
			if(sourceNode.userData.saveModifications) targetNode.userData.saveModifications = JSON.parse(JSON.stringify(sourceNode.userData.saveModifications));
			swapNodeReferences();

			for(const modificationType of Object.values(SaveModificationTypes))
			{
				applyModification(modificationType);
			}
		}
		else if(sourceNode.userData.saveModifications)
		{
			targetNode.userData.saveModifications = JSON.parse(JSON.stringify(sourceNode.userData.saveModifications));
			//if the replaced node is marked as reference, we must use the same guid for our new node. otherwise, a different guid won't matter.
			if(sourceNode.userData.saveModifications.includes(SaveModificationTypes.Reference)) 
			{
				swapNodeReferences();
			}

			for(const saveModification of sourceNode.userData.saveModifications)
			{
				applyModification(saveModification);
			}

			if(!sourceNode.userData.saveModifications.includes(SaveModificationTypes.Position)) 
			{
				applyModification(SaveModificationTypes.Position);
			}
			if(!sourceNode.userData.saveModifications.includes(SaveModificationTypes.Rotation)) 
			{
				applyModification(SaveModificationTypes.Rotation);
			}
			if(!sourceNode.userData.saveModifications.includes(SaveModificationTypes.Scale)) 
			{
				applyModification(SaveModificationTypes.Scale);
			}
		}	
		else
		{
			applyModification(SaveModificationTypes.Position);
			applyModification(SaveModificationTypes.Rotation);
			applyModification(SaveModificationTypes.Scale);
		}
	}

	swapModelTrackerReferences(oldToNew: boolean, newAssetId: string, oldAssetId: string)
	{
		for(const tracker of this.modelTrackersWithAssetReference)
		{
			tracker.setModel(oldToNew ? newAssetId : oldAssetId);
		}

		const cps = Scenario.getCPS()!;
		for(const trackerSwapData of this.modelTrackersWithSceneSelection)
		{
			const trackerToAdd = oldToNew ? trackerSwapData.newTracker : trackerSwapData.originalTracker;
			const trackerToRemove = oldToNew ? trackerSwapData.originalTracker : trackerSwapData.newTracker;

			if(trackerToRemove)
			{
				if(trackerToAdd)
				{
					cps.trackers!.push(trackerToAdd.userData.guid);
					Engine.getInstance().addObject(trackerToAdd, ARTracker.trackersRoot, cps.sceneId, true);

					const atIndex = ARTracker.trackersRoot.children.indexOf(trackerToRemove!);
					ARTracker.trackersRoot.children.splice(atIndex, 0, ARTracker.trackersRoot.children.splice(ARTracker.trackersRoot.children.length-1, 1)[0]);
				}
							
				cps.trackers = cps.trackers!.filter((id : string) => id !== trackerToRemove.userData.guid);
				Engine.getInstance().removeObject(trackerToRemove, false, cps.sceneId);
			}
			else
			{
				assert(trackerToAdd);
				cps.trackers!.push(trackerToAdd!.userData.guid);
				Engine.getInstance().addObject(trackerToAdd!, ARTracker.trackersRoot, cps.sceneId, true);
			}
		}

		document.dispatchEvent(new CustomEvent(CustomEventTypes.onTrackerListUpdated));
	}

	swapTimelineReferences(oldToNew: boolean) : void
	{
		for(const swapData of this.timelineSwapDatabase)
		{	
			if(oldToNew)
			{
				swapData.timelineObject.setReference(swapData.newSceneObject ?? null);
			}
			else
			{
				swapData.timelineObject.setReference(swapData.originalSceneObject);
			}
		}
	}

	swapCalloutReferences(oldToNew: boolean) : void
	{
		for(const swapData of this.calloutSwapDatabase)
		{
			const fromObject = oldToNew ? swapData.originalSceneObject : swapData.newSceneObject;
			const callout = Callout.getCallout(fromObject.userData.guid) as Callout;
			const toObject = oldToNew ? swapData.newSceneObject : swapData.originalSceneObject;
			toObject.add(callout);
		}
	}

	swapMaterialReferences(oldToNew: boolean) : void
	{
		for(const swapData of this.materialSwapDatabase)
		{
			const keptMaterial = oldToNew ? swapData.newMaterialAsset : swapData.originalMaterialAsset;
			MaterialAsset.overrideObjectGuidMaterialAssetReference(swapData.objectGuid, keptMaterial);
		}

		for(const swapData of this.materialInstanceSwapDatabase)
		{
			MaterialAsset.overrideMaterialInstanceObjectReference(swapData.originalSceneObject, swapData.newSceneObject);
		}
	}

	verify(oldAssetId: string, newAssetId: string) : boolean
	{
		return 	oldAssetId !== newAssetId && 
				isString(oldAssetId) && isString(newAssetId);
	}
}
